import type { UserRole } from '@alpha-edge/shared'
import type { Features } from '../../hooks/use-features'

export interface NavItem {
  readonly path: string
  readonly label: string
  readonly roles?: readonly UserRole[]
  readonly featureFlag?: keyof Features
  readonly children?: readonly NavItem[]
}

export interface NavSection {
  readonly key: string
  readonly header: string
  readonly items: readonly NavItem[]
  // Rarely-used sections start collapsed. Ignored when the visible nav is
  // sparse (see useNavCollapse) so low-privilege users aren't left staring
  // at a wall of closed headers.
  readonly defaultCollapsed?: boolean
}

// Edge / portable deployment nav -- lab workflows, records, and admin.
// Grouping follows docs/planning/UI-Workflow-Redesign.md Phase W1:
// daily lab flows first, security/account chrome collapsed by default.
export const EDGE_NAV_SECTIONS: readonly NavSection[] = [
  {
    key: 'edge.workflows',
    header: 'Workflows',
    items: [
      { path: '/', label: 'Dashboard' },
      { path: '/accessioning', label: 'Accession', roles: ['admin', 'tech', 'lab_tech'] },
      { path: '/testing', label: 'Tests', roles: ['admin', 'tech', 'lab_tech'] },
      {
        path: '/qc',
        label: 'QC',
        roles: ['admin', 'tech', 'lab_tech'],
        featureFlag: 'qc',
        children: [
          { path: '/qc/review', label: 'Review' },
          { path: '/qc/lots', label: 'Lots' },
          { path: '/qc/levey-jennings', label: 'Charts' },
          { path: '/qc/configurations', label: 'Configurations' },
          { path: '/qc/programs', label: 'Programs' },
        ],
      },
    ],
  },
  {
    key: 'edge.records',
    header: 'Records',
    items: [
      { path: '/patients', label: 'Patients' },
      { path: '/orders', label: 'Orders', roles: ['admin', 'tech', 'lab_tech', 'provider'] },
      { path: '/results', label: 'Results' },
      {
        path: '/devices',
        label: 'Devices',
        roles: ['admin', 'tech', 'lab_tech'],
        children: [{ path: '/devices/errors', label: 'Error Log', roles: ['admin', 'tech'] }],
      },
    ],
  },
  {
    key: 'edge.admin',
    header: 'Admin',
    items: [
      { path: '/settings', label: 'Settings', roles: ['admin'] },
      { path: '/admin/ehr-integrations', label: 'EHR Integrations', roles: ['admin'], featureFlag: 'ehr' },
      { path: '/admin/logs', label: 'System Logs', roles: ['admin'] },
      { path: '/admin/events', label: 'Events', roles: ['admin', 'customer_admin'] },
      { path: '/admin/audit', label: 'Audit Trail', roles: ['admin', 'customer_admin'] },
    ],
  },
  {
    key: 'edge.security',
    header: 'Security',
    defaultCollapsed: true,
    items: [
      { path: '/admin/support-access-report', label: 'Support Access Report', roles: ['admin'] },
      { path: '/admin/medflow-staff', label: 'Staff Access', roles: ['admin'] },
      { path: '/admin/secrets', label: 'Credential Vault', roles: ['admin', 'customer_admin'] },
      { path: '/admin/archive', label: 'Archive & Retention', roles: ['admin', 'customer_admin'] },
    ],
  },
  {
    key: 'edge.account',
    header: 'Account',
    defaultCollapsed: true,
    items: [
      {
        path: '/licenses',
        label: 'My Environments',
        roles: ['customer_admin'],
        children: [{ path: '/licenses/alerts', label: 'Alerts' }],
      },
      { path: '/my-organization', label: 'My Organization', roles: ['customer_admin'] },
      { path: '/my-profile', label: 'My Profile' },
    ],
  },
]

// Central deployment nav -- daily operations (monitoring, customer
// support) first, fleet management next, then occasional/rare sections
// collapsed by default.
export const CENTRAL_NAV_SECTIONS: readonly NavSection[] = [
  {
    key: 'central.operations',
    header: 'Operations',
    items: [
      { path: '/fleet', label: 'Overview', roles: ['medflow_admin'] },
      { path: '/fleet/alerts', label: 'Alerts', roles: ['customer_admin', 'medflow_admin'] },
      {
        path: '/fleet/devices',
        label: 'Devices',
        roles: ['customer_admin', 'medflow_admin'],
        children: [{ path: '/devices/errors', label: 'Error Log', roles: ['medflow_admin'] }],
      },
      { path: '/fleet/events', label: 'Events', roles: ['medflow_admin'] },
      { path: '/fleet/logs', label: 'Logs', roles: ['medflow_admin'] },
    ],
  },
  {
    key: 'central.support',
    header: 'Support',
    items: [
      { path: '/fleet/support-requests', label: 'Support Requests', roles: ['medflow_admin'] },
      { path: '/fleet/support-sessions', label: 'Support Sessions', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/fleet/staff-grants', label: 'Staff Grants', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/fleet/my-edges', label: 'My Active Edges', roles: ['medflow_admin'] },
      {
        path: '/admin/remote-access',
        label: 'Remote Access',
        roles: ['customer_admin', 'medflow_admin'],
        featureFlag: 'remote_access',
      },
    ],
  },
  {
    key: 'central.fleet',
    header: 'Fleet',
    items: [
      { path: '/fleet/customers', label: 'Customers', roles: ['medflow_admin'] },
      { path: '/licenses', label: 'Environments', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/fleet/topology', label: 'Topology', roles: ['medflow_admin'] },
      { path: '/fleet/provision', label: 'Provision Node', roles: ['medflow_admin'] },
      { path: '/fleet/provisioning', label: 'Provision Instrument', roles: ['medflow_admin'] },
      { path: '/fleet/udm-rollouts', label: 'UDM Rollouts', roles: ['medflow_admin'] },
      { path: '/fleet/backups', label: 'Backups', roles: ['medflow_admin'] },
      { path: '/fleet/migration', label: 'Migration', roles: ['customer_admin', 'medflow_admin'] },
      {
        path: '/admin/containers',
        label: 'Containers',
        roles: ['customer_admin', 'medflow_admin'],
        featureFlag: 'hostContainerAdmin',
      },
    ],
  },
  {
    key: 'central.insights',
    header: 'Insights',
    defaultCollapsed: true,
    items: [
      { path: '/admin/meta-analytics', label: 'Meta Analytics', roles: ['medflow_admin'] },
      { path: '/admin/reports', label: 'Reports & Dashboards', roles: ['medflow_admin'] },
    ],
  },
  {
    key: 'central.audit',
    header: 'Audit',
    defaultCollapsed: true,
    items: [
      { path: '/fleet/audit', label: 'Audit Log', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/fleet/local-audit', label: 'Local Audit Trail', roles: ['medflow_admin'] },
    ],
  },
  {
    key: 'central.configuration',
    header: 'Configuration',
    defaultCollapsed: true,
    items: [
      { path: '/admin/agent-tokens', label: 'Agent Tokens', roles: ['medflow_admin'] },
      { path: '/admin/external-api-keys', label: 'External API Keys', roles: ['medflow_admin'] },
      { path: '/admin/fax-config', label: 'Fax Config', roles: ['medflow_admin'] },
      {
        path: '/admin/fax-import-logs',
        label: 'Fax Import Logs',
        roles: ['medflow_admin'],
        featureFlag: 'fax_ingest' as keyof Features,
      },
      { path: '/admin/offsite-backup', label: 'Backup Config', roles: ['medflow_admin'] },
      { path: '/admin/provisioning-config', label: 'Provisioning Config', roles: ['medflow_admin'] },
      { path: '/admin/rate-limit', label: 'API Rate Limit', roles: ['medflow_admin'] },
      { path: '/fleet/agent-suggestions', label: 'Agent Suggestions', roles: ['medflow_admin'] },
      {
        path: '/admin/analytics-sharing',
        label: 'Analytics Sharing',
        roles: ['customer_admin'],
        featureFlag: 'data_sharing_opt_out',
      },
      {
        path: '/admin/offsite-backup',
        label: 'Off-Site Backup',
        roles: ['customer_admin'],
        featureFlag: 'offsite_backup',
      },
    ],
  },
  {
    key: 'central.account',
    header: 'Account',
    defaultCollapsed: true,
    items: [
      { path: '/my-organization', label: 'My Organization', roles: ['customer_admin'] },
      { path: '/my-profile', label: 'My Profile' },
    ],
  },
]

export const isPathActive = (itemPath: string, currentPath: string): boolean => {
  if (itemPath === '/') return currentPath === '/'
  return currentPath.startsWith(itemPath)
}

// Longest-prefix match across every item and child path. A plain
// first-match walk would pin '/fleet' (Overview) as active for every
// /fleet/* route and force the Operations section open on, say,
// /fleet/audit, which belongs to the Audit section.
export function findActiveSectionKey(sections: readonly NavSection[], currentPath: string): string | null {
  let bestKey: string | null = null
  let bestLength = -1
  for (const section of sections) {
    for (const item of section.items) {
      const paths = [item.path, ...(item.children ?? []).map((child) => child.path)]
      for (const path of paths) {
        if (isPathActive(path, currentPath) && path.length > bestLength) {
          bestLength = path.length
          bestKey = section.key
        }
      }
    }
  }
  return bestKey
}

// Case-insensitive substring filter for the quick-jump box. Runs over
// already-visibility-filtered sections so role- or flag-hidden entries can
// never surface. A matching item keeps all its children; a matching child
// keeps its parent for context but drops non-matching siblings.
export function filterNavSections(sections: readonly NavSection[], query: string): readonly NavSection[] {
  const q = query.trim().toLowerCase()
  if (!q) return sections
  return sections
    .map((section) => ({
      ...section,
      items: section.items.flatMap((item): readonly NavItem[] => {
        if (item.label.toLowerCase().includes(q)) return [item]
        const matchingChildren = (item.children ?? []).filter((child) => child.label.toLowerCase().includes(q))
        if (matchingChildren.length > 0) return [{ ...item, children: matchingChildren }]
        return []
      }),
    }))
    .filter((section) => section.items.length > 0)
}
